import React, { useState, useEffect } from "react";
import styled from "styled-components";
import CalendarImg from "lib/images/calendar.svg";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse
} from "@material-ui/core";
import { ControlPoint, RemoveCircleOutline } from "@material-ui/icons";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Takeaway from "../components/Takeaway";
import LoanTerms from "../components/LoanTerms";
import DonutChart from "../components/DonutChart";
import { PoweredByFooter } from "../components";

const Wrapper = styled.div`
  max-width: 631px;
  margin-left: auto;
  margin-right: auto;
  background:#FFFFFF;
`;

const Part1 = styled.div`
  padding: 24px;
  display: flex;
`;

const Half = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Schedule = styled.div`
  padding-left: 14px;
  padding-top: 16px;
  margin-top: 8px;
  margin-left: 26px;
  border-top: 1px solid #dadce0;
`;

const ScheduleTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
`;

const ScheduleItem = styled.div`
  font-size: 10px;
  margin: 4px 0;
  display: flex;
  align-items: center;
`;

const ScheduleString = styled.span`
  margin-left: 8px;
`;

const DetailTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin: 14px 0;
  padding: 0 16px;
`;

const Detail = styled.div`
  background: #f4f4f4;
  padding: 0 0 24px 0;
  border-top: 1px solid #dadce0;
  border-bottom: 1px solid #dadce0;
`;

const StyledTable = styled(Table)`
  && {
    font-family: Work Sans;
    display: block;
    overflow: hidden;
  }
`;

const StyledHeaderCell = styled(TableCell)`
  && {
    font-size: 10px;
    font-weight: 600;
    color: #000;
    border: none;
    text-align: center;
    padding: 12px;
    padding-bottom:0px;
    line-height: 12px;
    &&:first-child {
      text-align: left;
      padding-left: 16px;
    }
  }
`;

const ProjectedReplacementRateRow = styled(TableRow)`
  && {
    height:20px;

    td {
      height:20px;
      padding:0px;
      padding-right:10px;
    }
    &&:last-child{
      padding-right:10px !important;
    }
  }
`;

const StyledEFTableCell = styled(TableCell)`
  && {
    color: #000;
    font-size: 10px;
    padding: 12px;
    font-weight: normal;
    text-align: center;
    line-height: 12px;
    min-width: 76px;
    border: none;
    &&:first-child {
      text-align: left;
      padding-left: 16px;
      .second {
        color: #000;
      }
    }
    .second {
      font-size: 8px;
      color: #5f6368;
    }
  }
`;

const CellTop = styled.div`
  display: flex;
  align-items: center;  
  justify-content: center;
`;

const TriangleUp = styled.div`
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 10px solid #50C40A;
  width: 0px;
  height: 0px;
  margin-right: 4px;
`;

const TriangleDown = styled.div`
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #c40a0a;
  width: 0px;
  height: 0px;
  margin-right: 4px;
`;

const AddCircle = styled(ControlPoint)`&& {
  font-size: 16px;
  color: #3C4D4F;
  margin-left: -1px;
  cursor: pointer;
}`;

const RemoveCircle = styled(RemoveCircleOutline)`&& {
  font-size: 16px;
  color: #3C4D4F;
  margin-left: -1px;
  cursor: pointer;
}`;

const PrepaymentPreview = ({ data, logo }) => {
  const [prepaymentLoans, setPrepaymentLoans] = useState([]);
  const [overflowSchedulesIsCollapsed, setOverflowSchedulesIsCollapsed] = useState(true);

  useEffect(() => {
    if (data) {
      setPrepaymentLoans(data);
    }
  }, [data]);

  return (
    <div>
      {
        prepaymentLoans && prepaymentLoans?.map(({ isHedge, deal, primaryColor, secondaryColor, footerText, loanTerms, schedules, prepayment, projections }) => {
          const tableHeaders = projections.headers;
          const tableData = projections.data;
          const projectedReplacementRate = projections.projectedReplacementRate;

          const StyledEFTableRow = styled(TableRow)`
            && {
              background-color: transparent;
            }
            &&:nth-child(3) {
              background-color: #${secondaryColor}40;
              .second {
                font-size: 8px;
                color: #000;
              }
            }
            &&:last-child {
              padding-right: 10px;
            }
          `;

          return (
            <Wrapper>
              <Header
                dealName={deal.name}
                loanName={deal.loanName}
                color={primaryColor}
                type="Prepayment"
                companyLogo={logo ?? ""}
              />
              <Part1>
                <Half>
                  <Takeaway
                    condition="If you were to sell or refinance today"
                    type="prepayment penalty"
                    amount={prepayment.prepaymentPenalty}
                    percent={prepayment.prepaymentPercent}
                    isHedge={isHedge}
                  />
                  <LoanTerms
                    title="LOAN TERMS"
                    dropshadowColor={primaryColor}
                    items={loanTerms}
                  />
                </Half>
                <Half>
                  <DonutChart
                    height={270}
                    title={["Total", "Prepayment Cost", prepayment.total]}
                    data={[
                      {
                        label: "OPB",
                        value: prepayment.opb_raw,
                        color: `#${primaryColor}`
                      },
                      {
                        label: "Prepayment Penalty",
                        value: prepayment.prepaymentPenalty_raw,
                        color: `#${secondaryColor}`
                      }
                    ]}
                  />
                  <Schedule>
                    <ScheduleTitle>PREPAYMENT SCHEDULE</ScheduleTitle>
                    {schedules?.map((schedule, index) => {
                      const MIN_DISPLAY_NUMBER = 3;
                      const remainingSchedulesNumber = schedules.length - MIN_DISPLAY_NUMBER;

                      return (
                        <>
                          {
                            index <= 2 &&
                              <>
                                <ScheduleItem className="sitem">
                                  <img src={CalendarImg} alt="icon" />
                                  <ScheduleString>{schedule}</ScheduleString>
                                </ScheduleItem>
                                {
                                  index === 2 && Boolean(remainingSchedulesNumber) &&
                                    <ScheduleItem>
                                      {overflowSchedulesIsCollapsed ? <AddCircle onClick={() => setOverflowSchedulesIsCollapsed(false)} /> : <RemoveCircle onClick={() => setOverflowSchedulesIsCollapsed(true)} />}
                                      <ScheduleString>
                                        {remainingSchedulesNumber}
                                        {" "}
                                        {overflowSchedulesIsCollapsed ? "more" : "fewer"}
                                        {" "}
                                        schedules
                                      </ScheduleString>
                                    </ScheduleItem>
                                }
                              </>
                          }
                          {
                            index > 2 && Boolean(remainingSchedulesNumber) && !overflowSchedulesIsCollapsed &&
                              <Collapse unmountOnExit in={!overflowSchedulesIsCollapsed}>
                                <ScheduleItem className="sitem">
                                  <img src={CalendarImg} alt="icon" />
                                  <ScheduleString>{schedule}</ScheduleString>
                                </ScheduleItem>
                              </Collapse>
                          }
                        </>
                      );
                    })}
                  </Schedule>
                </Half>
              </Part1>
              <DetailTitle>PREPAYMENT PROJECTIONS</DetailTitle>
              <Detail>
                <StyledTable>
                  <TableHead>
                    <TableRow>
                      {tableHeaders.map(header => <StyledHeaderCell>
                        {header.value1}
                      </StyledHeaderCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <ProjectedReplacementRateRow>
                      {projectedReplacementRate.map(rr => <StyledHeaderCell>
                        {rr.value1}
                      </StyledHeaderCell>)}
                    </ProjectedReplacementRateRow>
                    {tableData.map((row, rowIndex) => <StyledEFTableRow>
                      {row.map((cell, cellIndex) => <StyledEFTableCell>
                        <CellTop style={rowIndex === 1 && cellIndex === 0 ? { fontWeight: 500, fontSize: 12, justifyContent: "flex-start" } : cellIndex === 0 ? { justifyContent: "flex-start" } : {}}>
                          {rowIndex === 0 && cellIndex === 0 &&
                            <TriangleUp />}
                          {rowIndex === 2 && cellIndex === 0 &&
                            <TriangleDown />}
                          {cell.value1}
                        </CellTop>
                        <div style={rowIndex === 1 && cellIndex === 0 ? { fontWeight: 500, fontSize: 12 } : {}} className="second">{cell.value2}</div>
                      </StyledEFTableCell>)}
                    </StyledEFTableRow>)}
                  </TableBody>
                </StyledTable>
              </Detail>
              {footerText !== null ? <Footer footerText={footerText} /> : <PoweredByFooter />}
            </Wrapper>
          );
        })
      }
    </div>
  );
};

export default PrepaymentPreview;
